import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, ShieldCheck, Heart } from "lucide-react";

export default function VardnadtvistSpotlight() {
    return (
        <section
            data-testid="vardnadstvist-spotlight"
            className="relative bg-[#0A0A0B] py-24 lg:py-32 border-t border-white/5 overflow-hidden"
        >
            {/* subtle gold gradient backdrop */}
            <div
                aria-hidden
                className="absolute inset-0 opacity-[0.06] pointer-events-none"
                style={{
                    background:
                        "radial-gradient(circle at 75% 50%, #C4A977 0%, transparent 55%)",
                }}
            />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    {/* Image column */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8 }}
                        className="lg:col-span-5 relative"
                    >
                        <div className="relative aspect-[4/5] max-w-md mx-auto lg:mx-0">
                            <img
                                src="/team/daniel-madsen.jpg"
                                alt="Daniel Madsen — Jurist & Notarius Publicus"
                                className="w-full h-full object-cover grayscale-[0.2]"
                            />
                            {/* Decorative gold corner */}
                            <div className="absolute -top-4 -left-4 w-20 h-20 border-t border-l border-[#C4A977]/60" />
                            <div className="absolute -bottom-4 -right-4 w-20 h-20 border-b border-r border-[#C4A977]/60" />
                        </div>
                    </motion.div>

                    {/* Content column */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        className="lg:col-span-7"
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#C4A977]/10 border border-[#C4A977]/40 rounded-sm mb-6">
                            <Sparkles size={12} className="text-[#C4A977]" />
                            <span className="text-[10px] tracking-[0.2em] uppercase text-[#C4A977] font-semibold">
                                Kostnadsfri inledande rådgivning
                            </span>
                        </div>

                        <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                            Vårdnadstvist —{" "}
                            <span className="italic text-[#C4A977]">
                                ditt barns röst räknas.
                            </span>
                        </h2>

                        <blockquote className="mt-10 pl-6 border-l-2 border-[#C4A977]/60">
                            <p className="font-serif italic text-xl lg:text-2xl text-[#F4F4F5]/95 leading-relaxed">
                                "I en vårdnadstvist står barnets bästa alltid i
                                centrum. Mitt jobb är att se till att den
                                principen får det utrymme den förtjänar — också
                                i rätten."
                            </p>
                            <footer className="mt-5 text-sm text-[#A1A1AA]">
                                <span className="text-[#F4F4F5] font-medium">
                                    Daniel Madsen
                                </span>{" "}
                                · Jur. kand., Notarius Publicus · BM Juridik
                            </footer>
                        </blockquote>

                        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="flex items-start gap-3 bg-[#111113] border border-white/10 px-5 py-4 rounded-sm">
                                <ShieldCheck
                                    size={18}
                                    className="text-[#C4A977] mt-0.5 flex-shrink-0"
                                />
                                <div>
                                    <p className="text-xs uppercase tracking-wider text-[#C4A977] font-semibold mb-1">
                                        Rättshjälp & Rättsskydd
                                    </p>
                                    <p className="text-xs text-[#A1A1AA] leading-relaxed">
                                        Vi söker ersättning så att kostnaden inte
                                        står i vägen.
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 bg-[#111113] border border-white/10 px-5 py-4 rounded-sm">
                                <Heart
                                    size={18}
                                    className="text-[#C4A977] mt-0.5 flex-shrink-0"
                                />
                                <div>
                                    <p className="text-xs uppercase tracking-wider text-[#C4A977] font-semibold mb-1">
                                        Empati & precision
                                    </p>
                                    <p className="text-xs text-[#A1A1AA] leading-relaxed">
                                        Erfaren ombuds­hantering med barnen i
                                        fokus.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <Link
                                to="/vardnadstvist"
                                data-testid="vardnadstvist-spotlight-cta"
                                className="inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#C4A977] text-black hover:bg-[#DBC08F] rounded-sm text-sm tracking-wide font-medium group transition-colors"
                            >
                                Boka kostnadsfri rådgivning
                                <ArrowRight
                                    size={14}
                                    className="transition-transform duration-300 group-hover:translate-x-1"
                                />
                            </Link>
                            <Link
                                to="/rattshjalp"
                                className="inline-flex items-center justify-center gap-3 px-7 py-4 border border-white/20 hover:border-[#C4A977] hover:text-[#C4A977] rounded-sm text-sm tracking-wide transition-colors"
                            >
                                Läs om rättshjälp
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
